import React from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';

/**
 * Get the left, top, and z-index style properties for the draggable item based on the graphical layout and desired placement
 *
 * @param initialPos
 * @param size [ dw, dh ]
 * @param coords { x, y, z }
 * @param containerSize { width, height }
 * @returns {{top: number, left: number}|{transform: string, top: number, left: number}|{transform: string, top: number, left: *}|{transform: string, top: number, left: number}|{transform: string, top: number, left: number}|{transform: string, top: number, left: *}|{transform: string, top: *, left: number}|{transform: string, top: *, left: number}|{transform: string, top: *, left: *}}
 */
function deriveStylesFromPosition(
  initialPos,
  [dw, dh],
  { x, y, z },
  [width, height]
) {
  let styles = {
    zIndex: 100,
    width: dw,
    height: dh,
  };

  // allow manually moved item to override initial position, while keeping transform properties
  if (x !== null && x !== undefined && y !== null && y !== undefined) {
    styles.left = x;
    styles.top = y;
  } else {
    switch (initialPos) {
      case 'nw':
        styles.left = 0;
        styles.top = 0;
        styles.transform = 'translate(0,0)';
        break;
      case 'n':
        styles.left = '50%';
        styles.top = 0;
        styles.transform = 'translateX(-50%)';
        break;
      case 'ne':
        styles.left = width - dw;
        styles.top = 0;
        break;
      case 'w':
        styles.left = 0;
        styles.top = height / 2;
        styles.transform = 'translateY(-50%)';
        break;
      case 'c':
        styles.left = '50%';
        styles.top = height / 2;
        styles.transform = 'translate(-50%,-50%)';
        break;
      case 'e':
        styles.left = width - dw;
        styles.top = height / 2;
        styles.transform = 'translateY(-50%)';
        break;
      case 'sw':
        styles.left = 0;
        styles.top = height - dh;
        styles.height = 'unset';
        break;
      case 's':
        styles.left = width / 2;
        styles.top = height;
        styles.transform = 'translate(-50%, -100%)';
        break;
      case 'se':
        styles.left = width - dw;
        styles.top = height - dh;
        break;
      default:
        styles.left = 0;
        styles.top = 0;
        styles.transform = 'translate(0,0)';
        break;
    }
  }

  return styles;
}

const Draggable = React.memo(
  ({
    children,
    id,
    position,
    itemType,
    coords,
    itemDims,
    containerDims,
    bubbleItem,
  }) => {
    const styles = deriveStylesFromPosition(
      position,
      itemDims,
      coords,
      containerDims
    );
    const [status, drag] = useDrag({
      item: {
        type: itemType,
        id: id,
        top: styles.top,
        left: styles.left,
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    });

    return (
      <div
        className={`drag-drop-item ${status.isDragging ? ' dragging' : ''}`}
        ref={drag}
        style={styles}
        draggable={true}
        onClick={() => bubbleItem(id)}
      >
        {children}
      </div>
    );
  }
);
Draggable.propTypes = {
  itemDims: PropTypes.array,
  containerDims: PropTypes.array,
};

export default Draggable;
